import * as Chart from 'chart.js';
import * as React from 'react';
import { Line } from 'react-chartjs-2';
import { ChartWrapper } from '../ChartWrapper';
import { DefaultChartProps, getOptionsForDefaultChartOptions } from '../DefaultChartProps';

export type DisplayType = 'Days' | 'Linear' | 'Manual' | 'None' | 'Time' | 'Weeks';

export interface ChartableLineDataItem {
    label: string;
    values: Chart.ChartPoint[];
    colour: string;
}

export interface ChartableLineData {
    data: ChartableLineDataItem[];
}

export function toChartableLineData<T>(
    obj: T | null | undefined,
    mappingFunc: (data: T) => ChartableLineData,
): ChartableLineData | null {
    return obj ? mappingFunc(obj) : null;
}

export interface LineChartProps extends DefaultChartProps {
    data: ChartableLineData;
    labels?: Chart.ChartData['labels'];
    displayType?: DisplayType;
    options?: Chart.ChartOptions;
}

export const LineChart: React.FC<LineChartProps> = ({
    data: { data },
    displayType = 'Linear',
    labels,
    options,
    otherOptions,
    ...props
}) => {
    let xAxes: Chart.ChartXAxe = {};

    if (displayType === 'Linear') {
        xAxes = {
            type: 'linear',
        };
    } else if (displayType === 'Time') {
        xAxes = {
            time: {
                tooltipFormat: 'DD/MM/YY',
            },
            type: 'time',
        };
    } else if (displayType === 'Weeks') {
        xAxes = {
            time: {
                displayFormats: {
                    day: 'MM/YY',
                },
                tooltipFormat: 'DD/MM/YY',
                unit: 'week',
            },
            type: 'time',
        };
    } else if (displayType === 'Days') {
        xAxes = {
            time: {
                displayFormats: {
                    day: 'MM/YY',
                },
                tooltipFormat: 'DD/MM/YY',
                unit: 'day',
            },
            type: 'time',
        };
    } else if (displayType === 'Manual') {
        // Do nothing
    }

    return (
        <ChartWrapper>
            <Line
                data={{
                    datasets: data.map(chartable => ({
                        backgroundColor: chartable.colour,
                        borderColor: chartable.colour,
                        data: chartable.values,
                        fill: false,
                        label: chartable.label,
                        pointBackgroundColor: 'transparent',
                        pointBorderColor: 'transparent',
                        pointHoverBackgroundColor: chartable.colour,
                        pointHoverBorderColor: chartable.colour,
                    })),
                    labels,
                }}
                legend={{
                    display: false,
                }}
                options={getOptionsForDefaultChartOptions({
                    helpers: {
                        xAxes,
                    },
                    options: {
                        ...options,
                        elements: {
                            line: {
                                tension: 0,
                            },
                        },
                        tooltips: {
                            callbacks: {
                                labelColor: item => ({
                                    backgroundColor: data[item.datasetIndex!].colour,
                                    borderColor: data[item.datasetIndex!].colour,
                                }),
                            },
                            intersect: false,
                            mode: 'index',
                        },
                    },
                    otherOptions,
                })}
                {...props}
            />
        </ChartWrapper>
    );
};
LineChart.displayName = 'LineChart';
